import { useCallback, type ButtonHTMLAttributes, type ReactNode } from "react";

import styles from "./RadioCard.module.css";

type ExcludedNativeProps =
  | "value"
  | "onClick"
  | "onSelect"
  | "type"
  | "role"
  | "aria-checked"
  | "children";

export type RadioCardProps<V extends string = string> = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  ExcludedNativeProps
> & {
  /** The value this card represents. Reported through `onSelect`. */
  value: V;
  /** Whether this card is the currently chosen option. */
  selected: boolean;
  /** Fired with `value` when the user activates the card. */
  onSelect: (value: V) => void;
  /** Headline of the card. Renders bold. */
  title: ReactNode;
  /** Optional supporting copy below the title. */
  description?: ReactNode;
  /**
   * Optional leading icon. Inherits `--COLOR-PRIMARY` when `selected` is
   * true; otherwise sits on the default text color.
   */
  icon?: ReactNode;
};

/**
 * `RadioCard` — selectable card primitive for "pick one from a few" UIs.
 *
 * Use inside a `<div role="radiogroup">` with one card per option. The card
 * renders as `<button role="radio" aria-checked>` so screen readers and
 * keyboard navigation treat the group as a radio set. Visuals are driven by
 * the design tokens (`--COLOR-PRIMARY`, `--grey-700`/`-800`); the only
 * visual escape hatch is `className`.
 *
 * Typical use:
 *
 *   <div role="radiogroup">
 *     <RadioCard
 *       value="cpa"
 *       selected={type === "cpa"}
 *       onSelect={setType}
 *       icon={<AimOutlined />}
 *       title="CPA"
 *       description="Pay per qualifying action."
 *     />
 *     ...
 *   </div>
 */
function RadioCard<V extends string = string>({
  value,
  selected,
  onSelect,
  title,
  description,
  icon,
  disabled,
  className = "",
  ...rest
}: RadioCardProps<V>) {
  const handleClick = useCallback(() => {
    if (disabled) return;
    onSelect(value);
  }, [disabled, onSelect, value]);

  const composed = [
    styles.card,
    selected ? styles.selected : null,
    icon ? styles.withIcon : null,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={handleClick}
      className={composed}
      disabled={disabled}
      {...rest}
    >
      {icon ? (
        <span className={styles.icon} aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <span
        className={styles.radio}
        aria-hidden="true"
        data-selected={selected || undefined}
      >
        <span className={styles.radioDot} />
      </span>
      <span className={styles.title}>{title}</span>
      {description ? (
        <span className={styles.description}>{description}</span>
      ) : null}
    </button>
  );
}

export default RadioCard;
